<script lang="ts">
	import type { Component, FileUpload } from '@prisma/client';
	import { onMount, onDestroy } from 'svelte';
	import { Embed, type ModalConfig } from '../wordpress';
	import { isEmbedded } from '../store';

	export let Config: FileUpload & Component;
	let inputEl: HTMLInputElement;
	let showInvalidFeedback = false;

	let file: File | null = null;
	let previewUrl: string | null = null;
	$: fileName = file
		? file.name.length > 100
			? file.name.slice(0, 95) + '...' + file.name.slice(-5)
			: file.name
		: '';
	let fileType = 'Image';
	const handleFiles = (event: Event) => {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			file = input.files[0];
			//Make sure files are not too big
			let MAX_FILE_SIZE = Config.MaxImageSize * 1024 * 1024; //1MB
			for (let i = 0; i < input.files.length; i++) {
				if (input.files[i].size > MAX_FILE_SIZE) {
					let content = `File ${input.files[i].name} is too large. Please upload a file smaller than ${Config.MaxImageSize}MB.`;
					if (isEmbedded) {
						let modalConf: ModalConfig = {
							title: 'Upload Error',
							content,
							buttons: [
								{
									text: 'Confirm',
									action: 'close'
								}
							]
						};
						Embed.send({ showModal: modalConf });
					} else {
						alert(content);
					}
					file = null;
					return;
				}
				//Confirm that file type is allowed
				console.log(input.files[i]);
				let type: string | undefined = input.files[i].type;
				let ext: string | undefined = '.' + input.files[i].name.split('.').pop();
				if (type.length > 0) {
					type = type.split('/')[0];
				}
				//console.log(type, Config.FileTypes.replaceAll('/*', ''));
				let accept = Config.FileTypes.replaceAll('/*', '').split(',');
				if (!accept.includes(type) && !accept.includes(ext)) {
					let content = `File type is not allowed. Please upload a file of type ${Config.FileTypes.replaceAll(
						',',
						', '
					).replaceAll('/*', '')}.`;
					if (isEmbedded) {
						let modalConf: ModalConfig = {
							title: 'Upload Error',
							content,
							buttons: [
								{
									text: 'Confirm',
									action: 'close'
								}
							]
						};
						Embed.send({ showModal: modalConf });
					} else {
						alert(content);
					}
					file = null;
					return;
				}
			}
			if (file.type.startsWith('image/')) {
				fileType = 'Image';
				previewUrl = URL.createObjectURL(file);
			} else {
				previewUrl = null;
				let parts = file?.name.split('.');
				fileType = parts.pop() || 'File';
			}
			showInvalidFeedback = false;
		}
	};

	onDestroy(() => {
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl); // Clean up the preview URL
		}
	});

	export async function validate() {
		if (file == null && Config.Required) {
			showInvalidFeedback = true;
			return false;
		}
		showInvalidFeedback = false;
		return true;
	}

	export function appendData(data: FormData) {
		if (file != null) {
			data.append(Config.DBCol + 'file', file);
			data.append(Config.DBCol + 'file_url', '');
		}
	}

	export function focus() {
		inputEl.focus();
	}
</script>

<div class="form-group row required">
	<div class="comp-title">{Config.Title}</div>
	<div class="row">
		<div class="col">
			{#if Config.HelpText}<span class="fs-7 snuggle squish">{Config.HelpText}</span>{/if}
			<div
				class="file-upload-wrapper fileUploadContainer"
				class:red-border={showInvalidFeedback}
				class:green-border={file !== null}
			>
				<div class="file-upload">
					<div class="file-upload-message">
						<i
							class="fas fa-cloud-upload-alt file-upload-cloud-icon"
							class:red-text={showInvalidFeedback}
						/>
						<p class="file-upload-default-message" class:red-text={showInvalidFeedback}>
							{Config.Prompt}
						</p>
						<p class="file-upload-main-error" />
					</div>
					<div class="file-upload-mask" />
					<ul class="file-upload-errors" />
					<input
						class="file-upload-input"
						type="file"
						name="file"
						accept={Config.FileTypes}
						on:change={handleFiles}
						bind:this={inputEl}
						class:is-invalid={showInvalidFeedback}
					/>
					{#if file != null}
						<div class="file-upload-previews">
							<div class="file-upload-preview">
								<div class="file-upload-render" style="text-align:center">
									{#if fileType == 'Image'}
										<img
											src={previewUrl}
											class="file-upload-preview-img"
											style="height: 100%; width: auto;"
											alt="Preview of {fileName}"
										/>
									{:else}
										<i class="fas fa-file file-upload-file-icon" /><span
											class="file-upload-extension">{fileType}</span
										>
									{/if}
								</div>
								<div class="file-upload-preview-details">
									<button
										class="btn btn-danger file-upload-remove-file-btn"
										on:click|preventDefault={() => {
											file = null;
											previewUrl = null;
										}}>Remove<i class="far fa-trash-alt ms-1" /></button
									>
									<div class="file-upload-details-container">
										<div class="file-uplod-preview-details-inner">
											<p class="file-upload-file-name">
												{fileName}
											</p>
											<p class="file-upload-preview-message" class:red-text={showInvalidFeedback}>
												Drag and drop or click to replace
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					{/if}
					<div class="invalid-tooltip" class:hidden={!showInvalidFeedback}>
						{Config.EmptyMsg ? Config.EmptyMsg : 'Please upload a file'}
					</div>
				</div>
			</div>
		</div>
	</div>
</div>

<style>
	.file-upload-wrapper {
		width: 100%;
	}
	.red-border {
		border-color: red;
	}
	.green-border {
		border-color: green;
		border-style: solid;
		border-width: 1px;
	}
	.red-text {
		color: red;
	}
</style>
